import { useTheme } from "@mui/material/styles";
import { usePrevious } from "@react-hookz/web";
import { useAtomValue } from "jotai";
import type { PageProps } from "next";
import { useRouter } from "next/router";
import type { CSSProperties, FunctionComponent, ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";
import type { PreloadedQuery } from "react-relay";

import { useResponsiveAdSlot } from "scmp-app/components/advertisement/hooks";
import { appBarScrollStickyAtom } from "scmp-app/components/app-bar/hooks/sticky/atoms";
import { HeaderBottomContainerElementID } from "scmp-app/pages/_app/consts";
import type { homePageQuery } from "scmp-app/queries/__generated__/homePageQuery.graphql";

import { appBarAtom } from "./atoms";
import { HideElementOnScrollEffectThreshold } from "./consts";
import { AppBarContextProvider } from "./contexts";
import { getShouldShowAdBelowAppBar } from "./helpers";
import { useAppBarSetStickyHeight } from "./hooks";
import {
  AdSlotContainer,
  Container,
  FullWidthBackground,
  HeaderBottomContainer,
  StyledHeader,
  StyledHomeHeader,
  StyledPlusHeader,
} from "./styles";
import type { AppBarVariant } from "./types";

export type Props = {
  children?: ReactNode;
  className?: string;
  hideTargetChildren?: ReactNode;
  pageProps?: PageProps & { preloadedQuery?: unknown };
};

export const AppBar: FunctionComponent<Props> = ({ children, className, pageProps }) => {
  const { adSlotHeight, bindAdSlotContainer, desktopAdSlot, mobileAdSlot, stickyStyle } =
    useResponsiveAdSlot(pageProps?.appBarConfiguration);

  const appBarValue = useAtomValue(appBarAtom);
  const { stickyScrollHeight = 0 } = useAtomValue(appBarScrollStickyAtom);
  const appBarStickyScrollHeight = useMemo(
    // added 2 trying to avoid double menu when scrolling up/down, should find a better way.
    () => stickyScrollHeight + adSlotHeight,
    [adSlotHeight, stickyScrollHeight],
  );
  const appBarVariant = useMemo<AppBarVariant>(
    () =>
      appBarValue?.scrollOverVariant ??
      pageProps?.appBarConfiguration?.variant ??
      "scmp/generic-light",
    [appBarValue?.scrollOverVariant, pageProps?.appBarConfiguration?.variant],
  );

  const hideElementOnScrollEffect = useStickyWithHideElementAfterScrollThreshold(
    HideElementOnScrollEffectThreshold.mobile,
    appBarStickyScrollHeight,
  );
  const stickyWithScrollAwayElement = useStickyWithScrollAwayElement(appBarStickyScrollHeight);

  const styles: CSSProperties = {
    ...(stickyStyle === "withHideElementAfterScrollThreshold"
      ? hideElementOnScrollEffect.targetContainerStyles
      : stickyWithScrollAwayElement.targetContainerStyles),
  };

  const { reference } = useAppBarSetStickyHeight();

  const headerConfiguration = {
    ...pageProps?.headerConfiguration,
    appBarStickyScrollHeight,
    appBarVariant,
  };

  const renderHeader = () => {
    switch (appBarVariant) {
      case "none":
        return null;
      case "scmp/home":
        return (
          <StyledHomeHeader
            headerConfiguration={headerConfiguration}
            initEdition={pageProps?.initEdition}
            preloadedQuery={pageProps?.preloadedQuery as PreloadedQuery<homePageQuery> | undefined}
          />
        );
      case "scmp/plus":
      case "scmp/plus/learnmore":
        return <StyledPlusHeader headerConfiguration={headerConfiguration} />;
      default:
        return <StyledHeader headerConfiguration={headerConfiguration} />;
    }
  };

  const renderLayout = () => {
    switch (appBarVariant) {
      default:
        return getShouldShowAdBelowAppBar(appBarVariant) ? (
          <>
            {renderHeader()}
            <HeaderBottomContainer id={HeaderBottomContainerElementID} />
            {children}
            <AdSlotContainer {...bindAdSlotContainer}>
              {mobileAdSlot}
              {desktopAdSlot}
            </AdSlotContainer>
          </>
        ) : (
          <>
            <AdSlotContainer {...bindAdSlotContainer} $hasBorderBottom={true}>
              {mobileAdSlot}
              {desktopAdSlot}
            </AdSlotContainer>
            {renderHeader()}
            <HeaderBottomContainer id={HeaderBottomContainerElementID} />
            {children}
          </>
        );
    }
  };

  return (
    <AppBarContextProvider>
      <Container
        $appBarVariant={appBarVariant}
        className={className}
        ref={reference}
        style={{ ...styles }}
      >
        <FullWidthBackground />
        {renderLayout()}
      </Container>
    </AppBarContextProvider>
  );
};

AppBar.displayName = "AppBar";

export const useIsScrollingOverAppBarThreshold = (scrollThreshold: number) => {
  const [isOver, setIsOver] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const over = window.scrollY > scrollThreshold;
      setIsOver(previous => (previous === over ? previous : over));
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, [scrollThreshold]);

  return isOver;
};

// This effect will hide the element with transition after the scroll container scroll passes a threshold
const useStickyWithHideElementAfterScrollThreshold = (
  scrollThreshold: number,
  targetElementHeight: number,
) => {
  const router = useRouter();
  const isGamePages = router.pathname.startsWith("/games");

  const theme = useTheme();
  const previousTargetElementHeight = usePrevious(targetElementHeight);

  const isScrollingOverThreshold = useIsScrollingOverAppBarThreshold(scrollThreshold);
  const shouldPauseTransition = previousTargetElementHeight !== targetElementHeight;

  // Do not sticky in game pages
  const targetContainerStyles: CSSProperties = isGamePages
    ? {
        position: "relative",
      }
    : {
        position: "sticky",
        transform: isScrollingOverThreshold ? `translateY(-${targetElementHeight}px)` : "none",
        transition: shouldPauseTransition
          ? "none"
          : theme.transitions.create("transform", {
              duration: 50,
            }),
      };

  return {
    targetContainerStyles,
  };
};

// This effect will make the container sticky after the scroll away element is complete scroll
// passed in the scroll container
const useStickyWithScrollAwayElement = (scrollAwayElementHeight: number) => {
  const shouldStick = useIsScrollingOverAppBarThreshold(scrollAwayElementHeight);

  const targetContainerStyles: CSSProperties = {
    position: shouldStick ? "sticky" : "relative",
    transform: shouldStick ? `translateY(-${scrollAwayElementHeight}px)` : "none",
  };

  return {
    targetContainerStyles,
  };
};
